import React, { useState, useEffect } from "react";
import { 
    FormGroup, 
    FormControl, 
    ControlLabel, 
    PageHeader, 
    Glyphicon, 
    Checkbox, 
    Button 
} from "react-bootstrap";
import { View } from "react-native";
import { Fade } from "react-awesome-reveal";
import { handleStripePayment, cancelStripePaymentSession } from "../utils/paymentHandler";
import {
    addDaysToDate,
    getUser,
} from "../utils/Common";
import { Checkmark } from "react-checkmark";
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import ReservePageLayout from "../components/ReservePageLayout";
import { brandColors } from "../brand/theme";
import "./reservations.css";
import DateFnsUtils from "@date-io/date-fns";
import styled from 'styled-components';

/** MUI theme for the date picker popup – uses brand colors (bedrock slate, beacon yellow, limestone) */
const reservationDatePickerTheme = createTheme({
  palette: {
    primary: {
      main: brandColors.bedrockSlate,
      contrastText: brandColors.beaconYellow,
    },
    secondary: {
      main: brandColors.graystone,
    },
    background: {
      default: brandColors.limestone,
      paper: brandColors.white,
    },
  },
  overrides: {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: brandColors.bedrockSlate,
        color: brandColors.beaconYellow,
      },
    },
    MuiPickersDay: {
      day: {
        color: brandColors.bedrockSlate,
      },
      daySelected: {
        backgroundColor: brandColors.bedrockSlate,
        color: brandColors.beaconYellow,
        '&:hover': {
          backgroundColor: brandColors.primaryHover,
          color: brandColors.beaconYellow,
        },
      },
      current: {
        color: brandColors.bedrockSlate,
      },
    },
    MuiPickersCalendarHeader: {
      switchHeader: {
        backgroundColor: brandColors.white,
        color: brandColors.bedrockSlate,
      },
      iconButton: {
        color: brandColors.bedrockSlate,
      },
    },
    MuiDialog: {
      paper: {
        backgroundColor: brandColors.white,
      },
    },
    MuiIconButton: {
      root: {
        color: brandColors.bedrockSlate,
        '&:hover': {
          backgroundColor: brandColors.limestone,
        },
      },
    },
  },
});

const BUTTON_DEFAULT_TEXT = "Please Complete the Form";

const CustomDateTimePicker = styled(DatePicker)`
    width: 100%;
    
    & .MuiInputBase-root {
        width: 100%;
    }
    
    & .MuiInputBase-input {
        font-size: 14px;
        border: 1px solid #CCC;
        padding: 10px 12px;
        cursor: pointer;
        text-align: center;
        width: 100%;
        height: 46px;
        box-sizing: border-box;
        border-radius: 6px;
        line-height: 1.42857143;
        background-color: #FFFFFF;
    }

    & .MuiInputBase-input:hover {
        background-color: var(--brand-limestone, #F2F0EB);
        border-color: var(--brand-bedrock-slate, #262626);
    }
    
    & .MuiInputBase-input:focus {
        border-color: var(--brand-bedrock-slate, #262626);
        outline: 0;
        box-shadow: 0 0 0 3px rgba(38, 38, 38, 0.15);
    }

    & .MuiInputBase-input::placeholder {
        color: var(--brand-graystone, #86878B);
    }
`;

export default function Reservations(props) {
    // const [firstName, setFirstName] = useState("");
    // const [lastName, setLastName] = useState("");
    const [fullName, setFullName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [emailActivated, setEmailActivated] = useState(false);
    const [checkedIsChecked, setCheckedIsChecked] = useState(false);
    const [buttonText, setButtonText] = useState(BUTTON_DEFAULT_TEXT);
    const [cancellable, setCancellable] = useState(false);
    const [stripePayment,setStripePayment] = useState(null);
    const [renderComplete, setRenderComplete] = useState(false);
    const [ignoreDateSelection, setIgnareDateSelection] = useState(false);
    // const tempDate = addDaysToDate(new Date(),14);
    const [dateSelected, setDateSelected] = useState(null);
    const [billingZip, setBillingZip] = useState("");
	const [mode, setMode] = useState("choice");


    useEffect(() => {
        if(
            fullName.length>0 &&
            companyName.length>0 &&
            email.length>0 &&
            phone.length>0 &&
            billingZip.length>0 &&
            (ignoreDateSelection || dateSelected) &&
            checkedIsChecked
        ) {
            setButtonText("Click to Provide Your Credit Card")
        }
    },[fullName,companyName,email,phone,billingZip,checkedIsChecked,ignoreDateSelection,dateSelected]);

    useEffect(() => {
        if(buttonText==="Payment Complete") {
            setTimeout(() => {
                setRenderComplete(true);
            },2000);
        }
    },[buttonText]);

    useEffect(() => {
        if(renderComplete) {
            setTimeout(() => {
                setRenderComplete(false);
                window.location.reload(false);
            },5000);
        }
    },[renderComplete]);

    async function cancelPayment(e) {
        if(stripePayment) {
            try {
                await cancelStripePaymentSession(stripePayment.id);
                setCancellable(false);
                setStripePayment(null);
                setButtonText(BUTTON_DEFAULT_TEXT);
                showError("Payment Cancelled");
            } catch(error) {
                showError(error.message);
            }
        }
    }

    function showError(msg) {
        props.setErrorStatus("error-message");
        props.setErrorMessage(msg);
        props.setShowError(true);
        setTimeout(function() {props.setShowError(false);},5000);
    }

    async function handleSubmitForPayment(e) {
        e.preventDefault();
        console.log("Submitting for Payment");
        
        let meta = { selectedDate: null };
        if(!ignoreDateSelection) {
            if(dateSelected)
                meta.selectedDate = dateSelected.toDateString();
        }

        const customerData = {
            fullName: fullName,
            companyName: companyName,
            email: email,
            phone: phone,
            address: {
                postal_code: billingZip,
            },
            metadata: meta,
        };

        await handleStripePayment({
            customerData,
            amount: 1.00,
            description: "This $1.00 fee reserves a free month of CAP service with Bedrock Wireless",
            onStatusChange: setButtonText,
            onError: (msg) => {
                setStripePayment(null); // Clear payment after error
                showError(msg);
            },
            onSuccess: () => {
                setCancellable(false);
                setStripePayment(null); // Clear payment after success
            },
            onCancellable: setCancellable,
            onPaymentStarted: setStripePayment
        });
    }

    function renderReservations() {
        if(renderComplete) {
            return (
                <View style={{paddingTop:"50px",flex:1,flexDirection:"row",justifyContent:"center"}}>
                    <div className="reservation-width-standard">
                        <div className="reservation-success-container">
                            <div className="reservation-success-checkmark">
                                <Checkmark size='xxlarge' color="#111111"/>
                            </div>
                            <br /><br />
                            <b>Thank you for your order!</b>
                            <br /><br />
                            <b>You will receive an email confirmation shortly.</b>
                        </div>
                    </div>
                </View>
            )
        } else {
            return (
                <>
					<div className="reservation-page-header">
						<PageHeader>
							{[1,2].includes((new Date()).getMonth()) ? "IBS: $1 First Month Reservation" : "The Build Show: $1 First Month Reservation"}
						</PageHeader>
					</div>
                    <div className="reservation-center-row">
                        <form className="reservation-width-standard info" onSubmit={e =>handleSubmitForPayment(e)}>
                            <div className="reservation-form-column">
                                {/* Row 1: Full Name and Company Name */}
                                <div className="reservation-form-row" style={{paddingBottom:"20px"}}>
                                    <FormGroup controlId="fullName" bsSize="large" className="reservation-form-group">
                                        <ControlLabel>Full Name</ControlLabel>
                                        <FormControl
                                            autoFocus
                                            value={fullName}
                                            onChange={e => setFullName(e.target.value)}
                                        />
                                    </FormGroup>
                                    <div className="reservation-form-row-with-spacing">
                                        <FormGroup controlId="companyName" bsSize="large" className="reservation-form-group">
                                            <ControlLabel>Company Name</ControlLabel>
                                            <FormControl
                                                value={companyName}
                                                onChange={e => setCompanyName(e.target.value)}
                                            />
                                        </FormGroup>
                                    </div>
                                </div>
                                {/* Row 2: Phone Number and Email */}
                                <div className="reservation-form-row">
                                    <FormGroup controlId="phone" bsSize="large" className="reservation-form-group">
                                        <ControlLabel>Phone Number</ControlLabel>
                                        <FormControl
                                            value={phone}
                                            type="tel"
                                            onChange={e => setPhone(e.target.value)}
                                        />
                                    </FormGroup>
                                    <div className="reservation-form-row-with-spacing">
                                        <FormGroup controlId="email" bsSize="large" className="reservation-form-group">
                                            <ControlLabel>Email</ControlLabel>
                                            <FormControl
                                                value={email}
                                                type="email"
                                                onFocus={e => setEmailActivated(true)}
                                                onChange={e => setEmail(e.target.value)}
                                            />
                                        </FormGroup>
                                    </div>
                                </div>
                                <View style={{flex:1,flexDirection:"row",paddingBottom:"0px"}}>
                                    <View style={{flex:1,flexDirection:"row"}}>
                                    </View>
                                    <View className="reservation-form-row-with-spacing">
                                        {emailActivated && (
                                            <Fade bottom>
                                                {/* <Glyphicon glyph="arrow-up" style={{paddingRight:"10px",color:"#297ACC"}} /> */}
                                                <b>We'll send your receipt here</b>
                                            </Fade>
                                        )}
                                    </View>
                                </View>
                                {/* Row 3: Billing ZIP (left) | Date selector + "I don't know yet" (right) */}
                                <div style={{paddingBottom:"10px",paddingTop:"10px"}}>
                                    <div className="reservation-form-row">
                                        <FormGroup controlId="billingZip" bsSize="large" className="reservation-form-group">
                                            <ControlLabel>Billing Zip Code</ControlLabel>
                                            <FormControl
                                                value={billingZip}
                                                inputMode="numeric"
                                                maxLength={10}
                                                onChange={e => setBillingZip(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                            />
                                        </FormGroup>
                                        <div className="reservation-form-row-with-spacing" style={{flexDirection:"column",alignItems:"stretch"}}>
                                            <ControlLabel style={{paddingBottom:"8px"}}>
                                                Do you know when you may need cameras?
                                            </ControlLabel>
                                            <div style={{display:"flex",flexDirection:"row",alignItems:"center",width:"100%",gap:"16px"}}>
                                                <FormGroup controlId="dateSelected" bsSize="large" className="reservation-form-group" style={{marginBottom:0,flex:1,minWidth:0}}>
                                                    <ThemeProvider theme={reservationDatePickerTheme}>
                                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                            <CustomDateTimePicker
                                                                format={"MMM d"}
                                                                value={dateSelected}
                                                                onChange={setDateSelected}
                                                                minDate={addDaysToDate(new Date(),7)}
                                                                InputProps={{ disableUnderline: true }}
                                                                inputProps={{ placeholder: "Click here to select a date" }}
                                                                disabled={ignoreDateSelection}
                                                            />
                                                        </MuiPickersUtilsProvider>
                                                    </ThemeProvider>
                                                </FormGroup>
                                                <FormGroup className="reservation-form-group" style={{marginBottom:0,flexShrink:0}}>
                                                    <div style={{display:"flex",alignItems:"center",height:"48px",paddingLeft:"10px"}}>
                                                        <Checkbox checked={ignoreDateSelection} onChange={e => setIgnareDateSelection(!ignoreDateSelection)}>
                                                            <div style={{paddingLeft:"20px"}}>I don't know yet</div>
                                                        </Checkbox>
                                                    </div>
                                                </FormGroup>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <View style={{flex:1, flexDirection:"row",paddingBottom:"30px",paddingLeft:"10px"}}>
                                    <Checkbox checked={checkedIsChecked} onChange={e => setCheckedIsChecked(!checkedIsChecked)}>
                                    <div style={{paddingLeft:"20px"}}>You agree to pay a no-obligation $1 reservation fee, redeemable for 1 month of service anytime in the next 12 months.</div>
                                    </Checkbox>
                                </View>
                                <View style={{flex:1, flexDirection:"row",justifyContent:"center"}}>
                                    <Button
                                        block
                                        type="submit"
                                        bsSize="large"
                                        className={buttonText === "Click to Provide Your Credit Card" ? "reservation-submit-button-ready" : ""}
                                        disabled={!checkedIsChecked || phone===""|| email==="" || fullName==="" || billingZip==="" || (!ignoreDateSelection && !dateSelected)}
                                    >
                                        {buttonText}
                                    </Button>
                                </View>
                                <div style={{paddingTop:"10px"}}>
                                    <Button bsSize="large" onClick={() => props.history.push('/reserve')}>Back</Button>
                                </div>
                            </div>
                        </form>
                    </div>
                    {cancellable && (
                        <View className="reservation-cancel-area">
                            <View className="reservation-cancel-button-container">
                                <Button
                                    block
                                    bsSize="large"
                                    bsStyle="danger"
                                    disabled={!cancellable}
                                    onClick={(e) => cancelPayment(e)}
                                >
                                    Cancel Transaction
                                </Button>
                            </View>
                        </View>
                    )}
                </>
            );
        }
    }

	function renderChoice() {
		return (
			<>
				<div className="reservation-page-header">
					<PageHeader>
						{[1,2].includes((new Date()).getMonth()) ? "IBS: $1 First Month Promotion" : "The Build Show: $1 First Month Promotion"}
					</PageHeader>
				</div>
			<View style={{flex:1,flexDirection:"column",justifyContent:"center",alignItems:"center",paddingTop:"40px"}}>
			    <View style={{width:"90%", maxWidth:"900px", paddingBottom:"30px"}}>
					<Button
						block
						bsSize="large"
						className="tablet-choice-button"
						onClick={() => props.history.push('/reserve/ready')}
					>
						I’m ready to start my $1 camera trial soon!
					</Button>
				</View>
				<View style={{width:"90%", maxWidth:"900px", paddingBottom:"30px"}}>
					<Button
						block
						bsStyle="primary"
						bsSize="large"
						className="tablet-choice-button tablet-choice-button-primary"
						onClick={() => setMode('reserve')}
					>
						I’ll be ready sometime in the next 12 months
					</Button>
				</View>
			</View>
			</>
		);
	}

    function renderBadAccess() {
        props.history.push(`/dashboard`)
    }

    return (
        <ReservePageLayout handleLogout={props.handleLogout}>
			{getUser().role==="PAYMENTS" || getUser().role==="ADMIN" ? (mode === 'choice' ? renderChoice() : renderReservations()) : renderBadAccess()}
		</ReservePageLayout>
    )
}
